import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  checkUserAvailabilityForAuth,
  getUserDetails,
  updateUserByAdmin,
  updateUserPasswordByAdmin,
  updateUserSystemRole,
  UserWithArtists,
  SystemAccessRole,
} from "../../services/api/user.service";
import { useUser } from "../../contexts/UserContext";
import { isTruthyApiFlag } from "../../utils/apiFlags";
import { toast } from "react-toastify";
import { ArrowLeft, Eye, EyeOff, KeyRound, Pencil, Save, X } from "lucide-react";
import { buildProfileUpdatePayload } from "../../utils/buildProfileUpdatePayload";
import {
  validateField as validateProfileField,
  validateProfileForm,
  type ProfileFormValidationErrors,
} from "../../utils/profileFormValidation";
import { PLATFORMS } from "../../constants/platforms";
import {
  mapAdminProfileForPayload,
  mapAdminProfileForValidation,
} from "../../utils/profileDataMapper";

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile: loggedInProfile } = useUser();
  const canManageSystemRole = isTruthyApiFlag(loggedInProfile?.isSuperAdmin);
  const [userData, setUserData] = useState<UserWithArtists | null>(null);
  const [profileData, setProfileData] = useState<UserWithArtists | null>(null);
  const [originalProfileData, setOriginalProfileData] = useState<UserWithArtists | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [distributors, setDistributors] = useState<string[]>([]);
  const [originalDistributors, setOriginalDistributors] = useState<string[]>([]);
  const [selectedDistributorDropdown, setSelectedDistributorDropdown] = useState("");
  const [customDistributorInput, setCustomDistributorInput] = useState("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [usernameAvailabilityStatus, setUsernameAvailabilityStatus] = useState<
    "idle" | "checking" | "available" | "taken"
  >("idle");
  const [mobileAvailabilityStatus, setMobileAvailabilityStatus] = useState<
    "idle" | "checking" | "available" | "taken"
  >("idle");
  const [formErrors, setFormErrors] = useState<ProfileFormValidationErrors>({
    username: "",
    firstName: "",
    lastName: "",
    artistName: "",
    spotifyUrl: "",
    mobile: "",
    address: "",
  });
  const [systemAccessRole, setSystemAccessRole] = useState<SystemAccessRole>("user");
  const [showSystemRoleConfirm, setShowSystemRoleConfirm] = useState(false);
  const [pendingSystemRole, setPendingSystemRole] = useState<SystemAccessRole | null>(null);
  const [systemRoleUpdating, setSystemRoleUpdating] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordUpdating, setPasswordUpdating] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const cloneUserData = (data: UserWithArtists): UserWithArtists =>
    JSON.parse(JSON.stringify(data));

  const isValidUserWithArtists = (data: unknown): data is UserWithArtists => {
    if (!data || typeof data !== "object") return false;
    const typed = data as UserWithArtists;
    return typeof typed.id === "number" && Array.isArray(typed.artists);
  };

  const clearFieldError = (field: keyof ProfileFormValidationErrors) => {
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  };

  const validationAvailability = React.useMemo(
    () => ({
      usernameTaken: usernameAvailabilityStatus === "taken",
      mobileTaken: mobileAvailabilityStatus === "taken",
    }),
    [usernameAvailabilityStatus, mobileAvailabilityStatus]
  );

  const validationInput = React.useMemo(() => {
    if (!profileData) {
      return {
        username: "",
        firstName: "",
        lastName: "",
        artistName: "",
        spotifyUrl: "",
        mobile: "",
        address: "",
      };
    }
    const mapped = mapAdminProfileForValidation(profileData);
    const payloadMapped = mapAdminProfileForPayload(profileData);
    return {
      ...mapped,
      mobile: payloadMapped.profile.mobile || "",
    };
  }, [profileData]);

  const validationResult = React.useMemo(() => {
    if (!profileData) {
      return {
        errors: {
          username: "",
          firstName: "",
          lastName: "",
          artistName: "",
          spotifyUrl: "",
          mobile: "",
          address: "",
        } as ProfileFormValidationErrors,
        isValid: false,
      };
    }
    const result = validateProfileForm(validationInput, validationAvailability);
    const errors = { ...result.errors, username: "" };
    return {
      errors,
      isValid: !Object.values(errors).some((error) => error !== ""),
    };
  }, [profileData, validationInput, validationAvailability]);

  const pendingPayload = React.useMemo(() => {
    if (!profileData || !originalProfileData) return {};
    const withCurrentDistributors = (() => {
      const cloned = cloneUserData(profileData);
      const primaryIdx = cloned.artists?.findIndex((artist) => artist.is_primary) ?? -1;
      const idxToUpdate = primaryIdx >= 0 ? primaryIdx : 0;
      if (cloned.artists?.[idxToUpdate]) {
        cloned.artists[idxToUpdate] = {
          ...cloned.artists[idxToUpdate],
          distributor: distributors.join(", "),
        };
      }
      return cloned;
    })();
    const withOriginalDistributors = (() => {
      const cloned = cloneUserData(originalProfileData);
      const primaryIdx = cloned.artists?.findIndex((artist) => artist.is_primary) ?? -1;
      const idxToUpdate = primaryIdx >= 0 ? primaryIdx : 0;
      if (cloned.artists?.[idxToUpdate]) {
        cloned.artists[idxToUpdate] = {
          ...cloned.artists[idxToUpdate],
          distributor: originalDistributors.join(", "),
        };
      }
      return cloned;
    })();
    return buildProfileUpdatePayload(
      mapAdminProfileForPayload(withCurrentDistributors),
      mapAdminProfileForPayload(withOriginalDistributors)
    );
  }, [profileData, originalProfileData, distributors, originalDistributors]);

  const canEditInputs = isEditMode && !isSaving;
  const hasChanges = Object.keys(pendingPayload).length > 0;
  const canSave = isEditMode && !isSaving && hasChanges && validationResult.isValid;
  const getInputClass = (hasError = false) =>
    `w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
      !canEditInputs ? "opacity-60 cursor-not-allowed" : ""
    } ${hasError ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-gray-900"}`;

  const handleUserFieldChange = (field: keyof UserWithArtists, value: string) => {
    if (!canEditInputs) return;
    setProfileData((prev) => (prev ? { ...prev, [field]: value } : prev));
    if (field === "firstname") clearFieldError("firstName");
    if (field === "lastname") clearFieldError("lastName");
    if (field === "mobile") clearFieldError("mobile");
    if (field === "address") clearFieldError("address");
  };

  const checkMobileAvailability = async (fullMobile: string) => {
    const mappedCurrent = mapAdminProfileForPayload({
      ...(profileData as UserWithArtists),
      mobile: fullMobile,
    });
    const mappedOriginal = mapAdminProfileForPayload(
      originalProfileData as UserWithArtists
    );
    const localMobile = mappedCurrent.profile.mobile;
    const countryCodeForCheck =
      mappedCurrent.countryCode || mappedOriginal.countryCode || "";
    const fullMobileForCheck = `${countryCodeForCheck}${localMobile}`;
    if (!localMobile || localMobile.length < 10 || localMobile.length > 12) {
      setMobileAvailabilityStatus("idle");
      return;
    }
    const originalFull = originalProfileData?.mobile || "";
    if (fullMobileForCheck === originalFull) {
      setMobileAvailabilityStatus("available");
      return;
    }

    setMobileAvailabilityStatus("checking");
    try {
      const userId = profileData?.id ? String(profileData.id) : "";
      const response = await checkUserAvailabilityForAuth("mobile", fullMobileForCheck, userId);
      if (response.exists) {
        setMobileAvailabilityStatus("taken");
        setFormErrors((prev) => ({
          ...prev,
          mobile: "Mobile number is already registered",
        }));
      } else {
        setMobileAvailabilityStatus("available");
      }
    } catch {
      setMobileAvailabilityStatus("idle");
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!profileData || !isEditMode) return;
      const fullMobile = profileData.mobile || "";
      if (fullMobile && fullMobile !== (originalProfileData?.mobile || "")) {
        checkMobileAvailability(fullMobile);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [profileData?.mobile, originalProfileData?.mobile, isEditMode]);

  const handleArtistFieldChange = (artistIndex: number, field: string, value: string) => {
    if (!canEditInputs) return;
    setProfileData((prev) => {
      if (!prev) return prev;
      const updatedArtists = [...(prev.artists || [])];
      const currentArtist = updatedArtists[artistIndex];
      if (!currentArtist) return prev;
      updatedArtists[artistIndex] = { ...currentArtist, [field]: value };
      return { ...prev, artists: updatedArtists };
    });
    if (field === "spotify_url") clearFieldError("spotifyUrl");
  };

  const handlePrimaryArtistNameChange = (value: string) => {
    if (!canEditInputs) return;
    setProfileData((prev) => {
      if (!prev) return prev;
      const updatedArtists = [...(prev.artists || [])];
      const primaryIdx = updatedArtists.findIndex((artist) => artist.is_primary);
      const idxToUpdate = primaryIdx >= 0 ? primaryIdx : 0;
      if (!updatedArtists[idxToUpdate]) return prev;
      updatedArtists[idxToUpdate] = {
        ...updatedArtists[idxToUpdate],
        artist_name: value,
      };
      return { ...prev, artists: updatedArtists };
    });
    clearFieldError("artistName");
  };

  const addDistributor = (distributor: string) => {
    if (!canEditInputs) return;
    if (!distributor.trim()) return;
    const trimmedDistributor = distributor.trim();
    if (distributors.some((d) => d.toLowerCase() === trimmedDistributor.toLowerCase())) {
      toast.error("Distributor already added");
      return;
    }
    const matchedPlatform = PLATFORMS.find(
      (p) => p.name.toLowerCase() === trimmedDistributor.toLowerCase()
    );
    const normalizedDistributor = matchedPlatform ? matchedPlatform.name : trimmedDistributor;
    setDistributors((prev) => [...prev, normalizedDistributor]);
    setSelectedDistributorDropdown("");
    setCustomDistributorInput("");
  };

  const removeDistributor = (index: number) => {
    if (!canEditInputs) return;
    setDistributors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDistributorSelect = (value: string) => {
    if (!canEditInputs) return;
    if (value === "Other") {
      setSelectedDistributorDropdown(value);
      return;
    }
    addDistributor(value);
  };

  const handleAddCustomDistributor = () => {
    if (!canEditInputs) return;
    if (customDistributorInput.trim()) {
      addDistributor(customDistributorInput.trim());
    } else {
      toast.error("Please enter a distributor name");
    }
  };

  const handleCancelEdit = () => {
    if (!originalProfileData) return;
    setProfileData(cloneUserData(originalProfileData));
    setDistributors([...originalDistributors]);
    setSelectedDistributorDropdown("");
    setCustomDistributorInput("");
    setIsEditMode(false);
    setUsernameAvailabilityStatus("idle");
    setMobileAvailabilityStatus("idle");
    setFormErrors({
      username: "",
      firstName: "",
      lastName: "",
      artistName: "",
      spotifyUrl: "",
      mobile: "",
      address: "",
    });
  };

  const extractUserIdFromPath = (): number | null => {
    const pathSegments = location.pathname.split('/').filter(segment => segment.trim() !== '');
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (!lastSegment) return null;
    const parsed = parseInt(lastSegment, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleSaveEdit = async () => {
    if (!profileData || !originalProfileData || isSaving) return;

    setFormErrors(validationResult.errors);
    if (!validationResult.isValid) {
      return;
    }

    const payload = pendingPayload;

    if (Object.keys(payload).length === 0) {
      toast.info("No changes made");
      return;
    }

    const userId = extractUserIdFromPath();
    if (!userId) {
      toast.error("Invalid user ID");
      return;
    }

    setIsSaving(true);
    try {
      await updateUserByAdmin(userId, payload);
      const updatedUser = await getUserDetails(userId);
      if (!isValidUserWithArtists(updatedUser)) {
        throw new Error("Unexpected response from server");
      }
      setUserData(updatedUser);
      setProfileData(cloneUserData(updatedUser));
      setOriginalProfileData(cloneUserData(updatedUser));
      setUsernameAvailabilityStatus("idle");
      setMobileAvailabilityStatus("idle");
      const primaryArtist = updatedUser.artists?.find((artist) => artist.is_primary) || updatedUser.artists?.[0];
      const nextDistributors = (primaryArtist?.distributor || "")
        .split(",")
        .map((d) => d.trim())
        .filter((d) => d.length > 0);
      setDistributors(nextDistributors);
      setOriginalDistributors([...nextDistributors]);
      setSelectedDistributorDropdown("");
      setCustomDistributorInput("");
      setIsEditMode(false);
      setFormErrors({
        username: "",
        firstName: "",
        lastName: "",
        artistName: "",
        spotifyUrl: "",
        mobile: "",
        address: "",
      });
      toast.success("User profile updated successfully");
    } catch (saveError: any) {
      toast.error(saveError?.message || "Failed to update user profile");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      // Extract numeric user ID from the last segment of the URL pathname
      const pathSegments = location.pathname.split('/').filter(segment => segment.trim() !== '');
      const lastSegment = pathSegments[pathSegments.length - 1];
      
      if (!lastSegment) {
        setError("User ID is required");
        setLoading(false);
        return;
      }

      const userIdNumber = parseInt(lastSegment, 10);
      
      if (isNaN(userIdNumber)) {
        setError("Invalid user ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const user = await getUserDetails(userIdNumber);
        if (!isValidUserWithArtists(user)) {
          throw new Error("Unexpected response from server");
        }
        setUserData(user);
        setProfileData(cloneUserData(user));
        setOriginalProfileData(cloneUserData(user));
        setSystemAccessRole(user.isSystemAdmin ? "admin" : "user");
        setUsernameAvailabilityStatus("idle");
        setMobileAvailabilityStatus("idle");
        const primaryArtist = user.artists?.find((artist) => artist.is_primary) || user.artists?.[0];
        const initialDistributors = (primaryArtist?.distributor || "")
          .split(",")
          .map((d) => d.trim())
          .filter((d) => d.length > 0);
        setDistributors(initialDistributors);
        setOriginalDistributors([...initialDistributors]);
        setSelectedDistributorDropdown("");
        setCustomDistributorInput("");
        setIsEditMode(false);
      } catch (err: any) {
        setError(err.message || "Failed to fetch user profile");
        toast.error(err.message || "Failed to fetch user profile");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [location.pathname]);

  const handleSystemAccessChange = (nextRole: SystemAccessRole) => {
    if (nextRole === systemAccessRole || systemRoleUpdating) {
      return;
    }
    setPendingSystemRole(nextRole);
    setShowSystemRoleConfirm(true);
  };

  const cancelSystemRoleChange = () => {
    setShowSystemRoleConfirm(false);
    setPendingSystemRole(null);
    if (userData) {
      setSystemAccessRole(userData.isSystemAdmin ? "admin" : "user");
    }
  };

  const confirmSystemRoleChange = async () => {
    if (!userData || !pendingSystemRole) {
      return;
    }

    try {
      setSystemRoleUpdating(true);
      const result = await updateUserSystemRole(userData.id, pendingSystemRole);
      const updated = {
        ...userData,
        systemRole: result.systemRole,
        isSystemAdmin: result.isSystemAdmin,
      };
      setUserData(updated);
      setProfileData(cloneUserData(updated));
      setOriginalProfileData(cloneUserData(updated));
      setSystemAccessRole(pendingSystemRole);
      setShowSystemRoleConfirm(false);
      setPendingSystemRole(null);
      toast.success(
        pendingSystemRole === "admin"
          ? "User is now a system admin. They may need to sign in again for access to update."
          : "System admin access removed. They may need to sign in again for changes to take effect."
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update system admin access");
      cancelSystemRoleChange();
    } finally {
      setSystemRoleUpdating(false);
    }
  };

  const closePasswordModal = () => {
    if (passwordUpdating) return;
    setShowPasswordModal(false);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handlePasswordUpdate = async () => {
    if (!userData) return;
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setPasswordUpdating(true);
    setPasswordError("");
    try {
      await updateUserPasswordByAdmin(userData.id, newPassword);
      toast.success("User password updated successfully");
      setShowPasswordModal(false);
      setNewPassword("");
      setConfirmPassword("");
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (err: any) {
      setPasswordError(err?.message || "Failed to update user password");
    } finally {
      setPasswordUpdating(false);
    }
  };

  const isEditingSelf =
    loggedInProfile?.id != null && userData?.id != null && loggedInProfile.id === userData.id;

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <div className="flex flex-col items-center justify-center mt-[5rem] min-h-[60vh]">
            <div className="w-12 h-12 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin mb-4"></div>
            <div className="text-gray-600 font-family-messina-sans text-lg">Loading user profile...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !userData || !profileData || !originalProfileData) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8 relative">
          <button
            onClick={() => navigate("/users-list")}
            className="mb-4 flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors font-family-messina-sans"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Users List</span>
          </button>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error || "User not found"}</div>
          </div>
        </div>
      </div>
    );
  }

  const primaryArtist = profileData.artists?.find(artist => artist.is_primary) || profileData.artists?.[0];
  const artistName = isEditMode
    ? primaryArtist?.artist_name || ""
    : primaryArtist?.artist_name || "N/A";
  const allArtists = profileData.artists || [];

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      {/* Main Content Area */}
      <div className="p-4 sm:p-6 md:p-8 relative">
        {/* Header with Back Button */}
        <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 py-3.5 mb-6 flex flex-col gap-3 border-b border-gray-300 bg-nova-dashboard-900 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <button
            onClick={() => navigate("/users-list")}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors font-family-messina-sans w-fit"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Users List</span>
          </button>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[1.4375rem] font-regular text-gray-900 font-family-messina-modern shrink-0">User Info</span>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 items-stretch w-full sm:w-auto">
              {!isEditMode ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(true)}
                    className="bg-white text-gray-900 px-4 py-3 rounded-lg-settings font-medium border border-gray-300 hover:bg-gray-100 transition-all duration-500 ease-in-out font-family-messina-sans w-full sm:w-auto flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20"
                  >
                    <KeyRound size={14} strokeWidth={2} />
                    <span className="text-[0.6875rem] leading-none">CHANGE PASSWORD</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditMode(true)}
                    className="bg-black text-white px-4 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20"
                  >
                    <Pencil size={14} strokeWidth={2} />
                    <span className="text-[0.6875rem] leading-none">EDIT</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="bg-white text-gray-900 text-[0.6875rem] px-8 py-3 rounded-lg-settings font-medium border border-gray-300 hover:bg-gray-100 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 flex items-center justify-center gap-2"
                  >
                    <X size={14} strokeWidth={2} />
                    <span className="leading-none">CANCEL</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    disabled={!canSave}
                    className="bg-black text-white text-[0.6875rem] px-8 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Save size={14} strokeWidth={2} className="opacity-80" />
                        <span className="leading-none">SAVING...</span>
                      </>
                    ) : (
                      <>
                        <Save size={14} strokeWidth={2} />
                        <span className="leading-none">SAVE CHANGES</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        {isEditMode && (
          <div className="mt-3 inline-flex items-center rounded-md bg-blue-100 text-blue-700 px-3 py-1 text-xs font-family-messina-sans">
            Editing User Profile
          </div>
        )}

        {showPasswordModal && createPortal(
          <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 font-family-messina-sans">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Change user password</h3>
              <p className="text-sm text-gray-600 mb-5">
                Set a new password for {userData.email}.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">New password</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(event) => {
                        setNewPassword(event.target.value);
                        setPasswordError("");
                      }}
                      disabled={passwordUpdating}
                      autoComplete="new-password"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/20 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((visible) => !visible)}
                      disabled={passwordUpdating}
                      aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-900 disabled:opacity-50"
                    >
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Confirm password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(event) => {
                        setConfirmPassword(event.target.value);
                        setPasswordError("");
                      }}
                      disabled={passwordUpdating}
                      autoComplete="new-password"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/20 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((visible) => !visible)}
                      disabled={passwordUpdating}
                      aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-900 disabled:opacity-50"
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  disabled={passwordUpdating}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePasswordUpdate}
                  disabled={passwordUpdating || !newPassword || !confirmPassword}
                  className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {passwordUpdating ? "Updating..." : "Update password"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {canManageSystemRole && (
          <div className="mt-6 mb-2 p-4 sm:p-5 border border-gray-200 rounded-xl bg-white">
            <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-3 sidebar-menu-item">
              System access
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
              <label className="flex items-center gap-2 cursor-pointer font-family-messina-sans text-sm text-gray-900">
                <input
                  type="radio"
                  name="system-access-role"
                  value="admin"
                  checked={systemAccessRole === "admin"}
                  disabled={systemRoleUpdating}
                  onChange={() => handleSystemAccessChange("admin")}
                  className="accent-black"
                />
                Admin
              </label>
              <label
                className={`flex items-center gap-2 font-family-messina-sans text-sm ${
                  isEditingSelf ? "text-gray-400 cursor-not-allowed" : "text-gray-900 cursor-pointer"
                }`}
              >
                <input
                  type="radio"
                  name="system-access-role"
                  value="user"
                  checked={systemAccessRole === "user"}
                  disabled={systemRoleUpdating || isEditingSelf}
                  onChange={() => handleSystemAccessChange("user")}
                  className="accent-black"
                />
                User
              </label>
            </div>
            {isEditingSelf && (
              <p className="mt-2 text-xs text-gray-500 font-family-messina-sans">
                You cannot remove your own system admin access.
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500 font-family-messina-sans">
              Changes apply to admin screens and API access. The user may need to sign in again.
            </p>
          </div>
        )}

        {showSystemRoleConfirm && pendingSystemRole &&
          createPortal(
          <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 font-family-messina-sans">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {pendingSystemRole === "admin" ? "Make system admin?" : "Remove system admin?"}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {pendingSystemRole === "admin"
                  ? `Are you sure you want to make ${userData.email} a system admin? They will get access to admin screens.`
                  : `Are you sure you want to remove system admin access from ${userData.email}?`}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={cancelSystemRoleChange}
                  disabled={systemRoleUpdating}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmSystemRoleChange}
                  disabled={systemRoleUpdating}
                  className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {systemRoleUpdating ? "Updating..." : "Proceed"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Account Information Section */}
        <div className="mb-6 sm:mb-8 mt-[3rem]">
          {/* <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Account Information</span> */}
          
          <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
            {/* Email */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                EMAIL
              </label>
              <input
                type="email"
                value={profileData.email || ""}
                readOnly
                className={`${getInputClass()} opacity-60 cursor-not-allowed`}
              />
              <p className="text-gray-500 text-xs mt-1">Email cannot be changed</p>
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                MOBILE NUMBER
              </label>
              <input
                type="tel"
                value={profileData.mobile || ""}
                readOnly={!canEditInputs}
                onChange={(e) => {
                  if (!canEditInputs) return;
                  const value = e.target.value;
                  setProfileData((prev) => (prev ? { ...prev, mobile: value } : prev));
                  clearFieldError("mobile");
                }}
                onBlur={() => {
                  if (!canEditInputs) return;
                  const localMobile = mapAdminProfileForPayload(profileData).profile.mobile || "";
                  setFormErrors((prev) => ({
                    ...prev,
                    mobile: validateProfileField(
                      "mobile",
                      localMobile,
                      validationAvailability
                    ),
                  }));
                }}
                className={getInputClass(Boolean(formErrors.mobile))}
              />
              {formErrors.mobile && (
                <p className="text-red-500 text-xs mt-1">{formErrors.mobile}</p>
              )}
            </div>

            {/* User ID */}
            {/* <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                USER ID
              </label>
              <input
                type="text"
                value={userData.id}
                readOnly
                className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
              />
            </div> */}

            {/* Role ID */}
            {/* <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                ROLE ID
              </label>
              <input
                type="text"
                value={userData.role_id || ""}
                readOnly
                className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
              />
            </div> */}

            {/* FB UID */}
            {/* <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                FB UID
              </label>
              <input
                type="text"
                value={userData.fbuid || ""}
                readOnly
                className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
              />
            </div> */}

            {/* Is Verified Email */}
            {/* <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                EMAIL VERIFIED
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={userData.is_verified_email ? "Yes" : "No"}
                  readOnly
                  className="flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                />
                <span className={`inline-block px-2 py-1 rounded text-xs ${
                  userData.is_verified_email ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                }`}>
                  {userData.is_verified_email ? "Verified" : "Not Verified"}
                </span>
              </div>
            </div> */}

            {/* Profile Picture */}
            {profileData.profile_pic && (
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  PROFILE PICTURE
                </label>
                <div className="mt-2">
                  <img
                    src={profileData.profile_pic}
                    alt="Profile"
                    className="w-24 h-24 md:w-32 md:h-32 rounded-md object-cover border border-gray-200"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}

            {/* Shirt Size */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                SHIRT SIZE
              </label>
              <select
                value={profileData.shirt_size || ""}
                disabled={!canEditInputs}
                onChange={(e) => handleUserFieldChange("shirt_size", e.target.value)}
                className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  !canEditInputs ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <option value="">Select shirt size</option>
                <option value="2XS">2XS</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="2XL">2XL</option>
                <option value="3XL">3XL</option>
                <option value="4XL">4XL</option>
                <option value="5XL">5XL</option>
              </select>
            </div>

            {/* Address */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                ADDRESS
              </label>
              <input
                type="text"
                value={profileData.address || ""}
                readOnly={!canEditInputs}
                onChange={(e) => {
                  if (!canEditInputs) return;
                  const value = e.target.value;
                  setProfileData((prev) => (prev ? { ...prev, address: value } : prev));
                  setFormErrors((prev) => ({
                    ...prev,
                    address: validateProfileField("address", value, validationAvailability),
                  }));
                }}
                onBlur={() => {
                  if (!canEditInputs) return;
                  setFormErrors((prev) => ({
                    ...prev,
                    address: validateProfileField(
                      "address",
                      profileData.address || "",
                      validationAvailability
                    ),
                  }));
                }}
                className={getInputClass(Boolean(formErrors.address))}
              />
              {formErrors.address && (
                <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>
              )}
            </div>

            {/* Address Section */}
            <div className="mt-8">
              {/* <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Address</span> */}
              
              {/* Address Fields - 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Country */}
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                    COUNTRY
                  </label>
                  <input
                    type="text"
                    value={profileData.country || ""}
                    readOnly={!canEditInputs}
                    onChange={(e) => handleUserFieldChange("country", e.target.value)}
                    className={getInputClass()}
                  />
                </div>

                {/* State */}
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                    STATE
                  </label>
                  <input
                    type="text"
                    value={profileData.state || ""}
                    readOnly={!canEditInputs}
                    onChange={(e) => handleUserFieldChange("state", e.target.value)}
                    className={getInputClass()}
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                    CITY
                  </label>
                  <input
                    type="text"
                    value={profileData.city || ""}
                    readOnly={!canEditInputs}
                    onChange={(e) => handleUserFieldChange("city", e.target.value)}
                    className={getInputClass()}
                  />
                </div>

                {/* Street */}
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                    STREET
                  </label>
                  <input
                    type="text"
                    value={profileData.street || ""}
                    readOnly={!canEditInputs}
                    onChange={(e) => handleUserFieldChange("street", e.target.value)}
                    className={getInputClass()}
                  />
                </div>

                {/* ZIP Code */}
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                    ZIP CODE
                  </label>
                  <input
                    type="text"
                    value={profileData.zip_code || ""}
                    readOnly={!canEditInputs}
                    onChange={(e) => handleUserFieldChange("zip_code", e.target.value)}
                    className={getInputClass()}
                  />
                </div>
              </div>
            </div>

            {/* Created At */}
            {/* <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                CREATED AT
              </label>
              <input
                type="text"
                value={formatDate(userData.created_at)}
                readOnly
                className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
              />
            </div> */}

            {/* Updated At */}
            {/* <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                UPDATED AT
              </label>
              <input
                type="text"
                value={formatDate(userData.updated_at)}
                readOnly
                className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
              />
            </div> */}
          </div>
        </div>

        {/* Artist Information Section */}
        <div className="mb-6 sm:mb-8 mt-[4rem]">
          <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Artist Information</span>
          
          <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
            {/* First Name */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                FIRST NAME
              </label>
              <input
                type="text"
                value={profileData.firstname || ""}
                readOnly={!canEditInputs}
                onChange={(e) => {
                  if (!canEditInputs) return;
                  const value = e.target.value;
                  setProfileData((prev) => (prev ? { ...prev, firstname: value } : prev));
                  setFormErrors((prev) => ({
                    ...prev,
                    firstName: validateProfileField("firstName", value, validationAvailability),
                  }));
                }}
                className={getInputClass(Boolean(formErrors.firstName))}
              />
              {formErrors.firstName && (
                <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                LAST NAME
              </label>
              <input
                type="text"
                value={profileData.lastname || ""}
                readOnly={!canEditInputs}
                onChange={(e) => {
                  if (!canEditInputs) return;
                  const value = e.target.value;
                  setProfileData((prev) => (prev ? { ...prev, lastname: value } : prev));
                  setFormErrors((prev) => ({
                    ...prev,
                    lastName: validateProfileField("lastName", value, validationAvailability),
                  }));
                }}
                className={getInputClass(Boolean(formErrors.lastName))}
              />
              {formErrors.lastName && (
                <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>
              )}
            </div>

            {/* Artist Name */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                ARTIST NAME
              </label>
              <input
                type="text"
                value={artistName}
                readOnly={!canEditInputs}
                onChange={(e) => {
                  if (!canEditInputs) return;
                  const value = e.target.value;
                  handlePrimaryArtistNameChange(value);
                  setFormErrors((prev) => ({
                    ...prev,
                    artistName: validateProfileField("artistName", value, validationAvailability),
                  }));
                }}
                className={getInputClass(Boolean(formErrors.artistName))}
              />
              {formErrors.artistName && (
                <p className="text-red-500 text-xs mt-1">{formErrors.artistName}</p>
              )}
            </div>
          </div>
        </div>

        {/* Artist Details Section */}
        {primaryArtist && (
          <div className="mb-6 sm:mb-8 mt-[4rem]">
            {/* <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Artist Details</span> */}
            
            <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
              {/* Artist ID */}
              {/* <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  ARTIST ID
                </label>
                <input
                  type="text"
                  value={primaryArtist.id || ""}
                  readOnly
                  className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                />
              </div> */}

              {/* Clickhouse Artist ID */}
              {/* <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  CLICKHOUSE ARTIST ID
                </label>
                <input
                  type="text"
                  value={primaryArtist.clickhouse_artist_id || ""}
                  readOnly
                  className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                />
              </div> */}

              {/* Role */}
              {/* <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  ROLE
                </label>
                <input
                  type="text"
                  value={primaryArtist.role || ""}
                  readOnly
                  className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                />
              </div> */}

              {/* Is Primary */}
              {/* <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  IS PRIMARY
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={primaryArtist.is_primary ? "Yes" : "No"}
                    readOnly
                    className="flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                  />
                  <span className={`inline-block px-2 py-1 rounded text-xs ${
                    primaryArtist.is_primary ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                  }`}>
                    {primaryArtist.is_primary ? "Primary" : "Secondary"}
                  </span>
                </div>
              </div> */}

              {/* Is Artist Mapped */}
              {/* <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  IS ARTIST MAPPED
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={primaryArtist.is_artist_mapped ? "Yes" : "No"}
                    readOnly
                    className="flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                  />
                  <span className={`inline-block px-2 py-1 rounded text-xs ${
                    primaryArtist.is_artist_mapped ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                  }`}>
                    {primaryArtist.is_artist_mapped ? "Mapped" : "Not Mapped"}
                  </span>
                </div>
              </div> */}

              {/* Artist Created At */}
              {/* <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  ARTIST CREATED AT
                </label>
                <input
                  type="text"
                  value={formatDate(primaryArtist.created_at)}
                  readOnly
                  className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                />
              </div> */}

              {/* Artist Updated At */}
              {/* <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  ARTIST UPDATED AT
                </label>
                <input
                  type="text"
                  value={formatDate(primaryArtist.updated_at)}
                  readOnly
                  className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                />
              </div> */}
            </div>
          </div>
        )}

        {/* Distribution Information Section */}
        {primaryArtist && (
          <div className="mb-6 sm:mb-8 mt-[4rem]">
            <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Distribution Information</span>
            
            <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  DISTRIBUTORS
                </label>

                {!isEditMode ? (
                  <input
                    type="text"
                    value={distributors.join(", ")}
                    readOnly
                    className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans opacity-60 cursor-not-allowed"
                  />
                ) : (
                  <>
                    {distributors.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {distributors.map((distributor, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-900 rounded-full text-sm font-family-messina-sans"
                          >
                            {distributor}
                            <button
                              type="button"
                              onClick={() => removeDistributor(index)}
                              disabled={!canEditInputs}
                              className={`ml-1 text-gray-500 hover:text-gray-700 focus:outline-none ${
                                !canEditInputs ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              aria-label={`Remove ${distributor}`}
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <select
                          value={selectedDistributorDropdown}
                          disabled={!canEditInputs}
                          onChange={(e) => handleDistributorSelect(e.target.value)}
                          className={`flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                            !canEditInputs ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          <option value="">Select distributor to add</option>
                          {PLATFORMS.filter(
                            (platform) =>
                              platform.name !== "Other" &&
                              !distributors.some(
                                (d) => d.toLowerCase() === platform.name.toLowerCase()
                              )
                          ).map((platform) => (
                            <option key={platform.id} value={platform.name}>
                              {platform.name}
                            </option>
                          ))}
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {selectedDistributorDropdown === "Other" && (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customDistributorInput}
                            disabled={!canEditInputs}
                            onChange={(e) => setCustomDistributorInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddCustomDistributor();
                              }
                            }}
                            placeholder="Enter custom distributor name"
                            className={`flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                              !canEditInputs ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                          />
                          <button
                            type="button"
                            onClick={handleAddCustomDistributor}
                            disabled={!canEditInputs}
                            className={`px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans ${
                              !canEditInputs ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!canEditInputs) return;
                              setSelectedDistributorDropdown("");
                              setCustomDistributorInput("");
                            }}
                            disabled={!canEditInputs}
                            className={`px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans ${
                              !canEditInputs ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>

                    {distributors.length === 0 && (
                      <p className="text-gray-500 text-xs mt-2">No distributors added yet</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Artist Account Links Section - Display for all artists */}
        {allArtists.length > 0 && (
          <div className="mb-6 sm:mb-8 mt-[4rem]">
            <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Artist Account Links</span>
            
            <div className="space-y-8 sm:space-y-10 font-family-messina-sans-regular">
              {allArtists.map((artist, artistIndex) => (
                <div key={artist.id || artistIndex} className="space-y-4 sm:space-y-6">
                  {/* YouTube URL */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      YOUTUBE URL
                    </label>
                    <input
                      type="url"
                      value={artist.youtube_url || ""}
                      placeholder="https://youtube.com/@yourchannel"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "youtube_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Spotify URL */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      SPOTIFY URL
                    </label>
                    <input
                      type="url"
                      value={artist.spotify_url || ""}
                      placeholder="https://open.spotify.com/artist/yourid"
                      readOnly={!canEditInputs}
                      onChange={(e) => {
                        if (!canEditInputs) return;
                        const value = e.target.value;
                        handleArtistFieldChange(artistIndex, "spotify_url", value);
                        if (artist.is_primary) {
                          setFormErrors((prev) => ({
                            ...prev,
                            spotifyUrl: validateProfileField(
                              "spotifyUrl",
                              value,
                              validationAvailability
                            ),
                          }));
                        }
                      }}
                      onBlur={() => {
                        if (!canEditInputs || !artist.is_primary) return;
                        setFormErrors((prev) => ({
                          ...prev,
                          spotifyUrl: validateProfileField(
                            "spotifyUrl",
                            artist.spotify_url || "",
                            validationAvailability
                          ),
                        }));
                      }}
                      className={getInputClass(artist.is_primary && Boolean(formErrors.spotifyUrl))}
                    />
                    {artist.is_primary && formErrors.spotifyUrl && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.spotifyUrl}</p>
                    )}
                  </div>

                  {/* Apple URL */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      APPLE URL
                    </label>
                    <input
                      type="url"
                      value={artist.apple_url || ""}
                      placeholder="https://music.apple.com/artist/yourid"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "apple_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Instagram Link */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      INSTAGRAM LINK / META
                    </label>
                    <input
                      type="url"
                      value={artist.instagram_url || ""}
                      placeholder="https://instagram.com/yourhandle"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "instagram_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Facebook */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      FACEBOOK
                    </label>
                    <input
                      type="url"
                      value={artist.facebook_url || ""}
                      placeholder="https://facebook.com/yourpage"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "facebook_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* TikTok Link */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      TIKTOK LINK
                    </label>
                    <input
                      type="url"
                      value={artist.tiktok_url || ""}
                      placeholder="https://tiktok.com/@yourhandle"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "tiktok_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* SoundCloud Link */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      SOUNDCLOUD LINK
                    </label>
                    <input
                      type="url"
                      value={artist.soundcloud_url || ""}
                      placeholder="https://soundcloud.com/yourhandle"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "soundcloud_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* X / Twitter */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      X / TWITTER
                    </label>
                    <input
                      type="url"
                      value={artist.twitter_url || ""}
                      placeholder="https://x.com/yourhandle"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "twitter_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Amazon Music */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      AMAZON MUSIC
                    </label>
                    <input
                      type="url"
                      value={artist.amazon_music_url || ""}
                      placeholder="https://music.amazon.com/artists/yourid"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "amazon_music_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Deezer */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      DEEZER
                    </label>
                    <input
                      type="url"
                      value={artist.deezer_url || ""}
                      placeholder="https://www.deezer.com/en/artist/yourid"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "deezer_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Audiomack */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      AUDIOMACK
                    </label>
                    <input
                      type="url"
                      value={artist.audiomack_url || ""}
                      placeholder="https://audiomack.com/yourhandle"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "audiomack_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Pandora */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      PANDORA
                    </label>
                    <input
                      type="url"
                      value={artist.pandora_url || ""}
                      placeholder="https://pandora.com/artist/your-name"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "pandora_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Tidal */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      TIDAL
                    </label>
                    <input
                      type="url"
                      value={artist.tidal_url || ""}
                      placeholder="https://tidal.com/browse/artist/yourid"
                      readOnly={!canEditInputs}
                      onChange={(e) => handleArtistFieldChange(artistIndex, "tidal_url", e.target.value)}
                      className={getInputClass()}
                    />
                  </div>

                  {/* Separator between artists */}
                  {artistIndex < allArtists.length - 1 && (
                    <div className="border-b border-gray-300 my-6"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
